import { useEffect, useRef, useState } from "react";
import type { AuctionState, RoomView } from "@grundbesitz/shared";
import { highestBid, minimumBid } from "@grundbesitz/engine";
import { auctionParticipants, auctionSubject, describeAuctionResult } from "../../lib/assets";
import { formatMoney } from "../../lib/format";
import { secondsUntil, useStore } from "../../lib/store";
import { Button } from "../ui/Button";
import { Sheet } from "../ui/Sheet";
import { PlayerChip } from "./assets/PlayerChip";
import { useTick } from "./assets/useTick";
import { BidControls } from "./auction/BidControls";
import { BidLog } from "./auction/BidLog";
import { Countdown } from "./auction/Countdown";
import { Subject } from "./auction/Subject";

const RESULT_MS = 4500;

/**
 * Opens by itself while `game.auction` exists. Escape or the close button hides it; a strip at the
 * top brings it back. When the auction ends, the result shows for a moment and the dialog closes.
 */
export function AuctionDialog({ room }: { room: RoomView }) {
  const game = room.game;
  const auction = game?.auction ?? null;
  const pid = room.you.playerId;
  const [dismissedId, setDismissedId] = useState<number | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const roomRef = useRef(room);
  roomRef.current = room;
  const lastAuction = useRef<AuctionState | null>(null);
  if (auction) lastAuction.current = auction;
  const hiddenRef = useRef(false);
  hiddenRef.current = auction === null ? false : dismissedId === auction.id;

  useEffect(() => {
    let timer: number | undefined;
    const off = useStore.getState().subscribeEvents((batch) => {
      for (const ev of batch.events) {
        if (ev.type !== "AUCTION_ENDED") continue;
        const current = roomRef.current;
        const winner = ev.winnerId ? (current.game?.players.find((p) => p.id === ev.winnerId)?.name ?? "Unbekannt") : null;
        const text = describeAuctionResult(ev, winner, lastAuction.current);
        if (hiddenRef.current) {
          useStore.getState().toast(text, "info");
          continue;
        }
        setResult(text);
        window.clearTimeout(timer);
        timer = window.setTimeout(() => setResult(null), RESULT_MS);
      }
    });
    return () => {
      off();
      window.clearTimeout(timer);
    };
  }, []);

  const running = auction !== null;
  const secondsLeft = secondsUntil(room.timers.auctionEndsAt);
  useTick(running && dismissedId === auction?.id, 500);

  if (!game) return null;

  const hidden = auction !== null && dismissedId === auction.id;
  const stripHigh = hidden ? highestBid(game) : null;
  const open = (auction !== null && !hidden) || result !== null;
  const onClose = () => {
    if (auction) setDismissedId(auction.id);
    setResult(null);
  };

  return (
    <>
      {auction && hidden && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-30 flex justify-center px-2 pt-[max(0.5rem,env(safe-area-inset-top))]">
          <div className="pointer-events-auto flex max-w-full items-center gap-3 rounded-2xl border border-accent bg-surface py-1.5 pl-4 pr-1.5 text-sm shadow-lg">
            <span className="min-w-0 leading-tight">
              <strong className="block truncate">Versteigerung: {auctionSubject(game, auction, pid).title}</strong>
              <span className="text-xs text-ink-2">
                {stripHigh ? `Höchstgebot ${formatMoney(stripHigh.amount)}` : "Noch kein Gebot"}
                {secondsLeft !== null ? `, noch ${secondsLeft} s` : ""}
              </span>
            </span>
            <Button
              variant="primary"
              className="min-h-11 shrink-0 px-4 py-1"
              aria-label="Versteigerung anzeigen"
              onClick={() => setDismissedId(null)}
            >
              Anzeigen
            </Button>
          </div>
        </div>
      )}

      <Sheet open={open} onClose={onClose} title={auction ? "Versteigerung" : "Versteigerung beendet"}>
        {auction ? (
          <Running room={room} auction={auction} previousResult={result} />
        ) : (
          <div className="space-y-3 py-2">
            <p role="status" className="font-display text-xl font-semibold leading-snug">
              {result}
            </p>
            <Button className="w-full" onClick={onClose}>
              Schließen
            </Button>
          </div>
        )}
      </Sheet>
    </>
  );
}

function Running({ room, auction, previousResult }: { room: RoomView; auction: AuctionState; previousResult: string | null }) {
  const game = room.game!;
  const pid = room.you.playerId;
  const high = highestBid(game);
  const min = minimumBid(game);
  const me = pid ? game.players.find((p) => p.id === pid) : undefined;
  const mine = pid ? auctionParticipants(game).find((p) => p.playerId === pid) : undefined;
  const canBid = Boolean(me && !me.eliminated && mine);
  const showControls = canBid && mine?.status !== "PASSED";

  let position: string;
  if (!pid || !me || me.eliminated) position = "Du schaust zu.";
  else if (!mine) position = "Du nimmst an dieser Versteigerung nicht teil.";
  else if (mine.status === "LEADING") position = `Du führst mit ${formatMoney(high?.amount ?? 0)}.`;
  else if (mine.status === "PASSED") position = "Du hast verzichtet. Du kannst nicht mehr einsteigen.";
  else if (mine.status === "DEBT") position = "Du hast Schulden und darfst nicht bieten.";
  else if (mine.lastBid !== null) position = `Du wurdest überboten (dein Gebot: ${formatMoney(mine.lastBid)}).`;
  else position = "Du bietest mit.";

  return (
    <div className="space-y-4">
      {previousResult && (
        <p role="status" className="rounded-lg bg-accent-soft px-3 py-2 text-sm font-semibold">
          Zuvor: {previousResult}
        </p>
      )}
      <Subject game={game} members={room.members} pid={pid} />

      <section aria-label="Stand der Versteigerung" className="space-y-2.5">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-ink-2">Höchstgebot</p>
            <p className={`font-display font-bold leading-none tabular-nums ${high ? "text-4xl" : "text-2xl"}`}>{high ? formatMoney(high.amount) : "Noch kein Gebot"}</p>
            {high && (
              <p className="mt-1 text-sm">
                <PlayerChip game={game} members={room.members} id={high.playerId} you={high.playerId === pid} />
              </p>
            )}
          </div>
          <div className="shrink-0 text-right">
            <p className="text-xs text-ink-2">Mindestgebot</p>
            <p className="text-lg font-semibold tabular-nums">{formatMoney(min)}</p>
          </div>
        </div>
        <Countdown endsAt={room.timers.auctionEndsAt} />
        <p className="text-sm">{position}</p>
        <p className="sr-only" aria-live="polite">
          {high ? `Höchstgebot ${formatMoney(high.amount)} von ${game.players.find((p) => p.id === high.playerId)?.name ?? ""}` : "Noch kein Gebot"}
        </p>
      </section>

      {showControls && pid && <BidControls game={game} pid={pid} autoFocus />}

      <BidLog game={game} members={room.members} pid={pid} />
      <p className="text-xs text-ink-2">Bieten und Verzichten gehen jederzeit, auch außerhalb deines Zugs.</p>
    </div>
  );
}
